import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axiosPost from '../helpers/axios-helper';
import config from '../constants/config';
import '../styles/Destinations.css';

export default function Destinations() {
  const [countries, setCountries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [query, setQuery] = useState('');

  const toAbsUrl = (url, fallback = '') => {
    if (!url) return fallback;
    if (/^https?:\/\//i.test(url)) return url;
    return `${config.api.url}${url}`;
  };

  const slugify = (str) => {
    try {
      return (str || '')
        .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .replace(/&/g, ' and ')
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');
    } catch { return ''; }
  };

  useEffect(() => {
    const controller = new AbortController();
    axiosPost(
      'countries/list',
      { 'Content-Type': 'application/json' },
      { include_inactive: 1 },
      (resp) => {
        try {
          const arr = Array.isArray(resp) ? resp : (Array.isArray(resp?.data) ? resp.data : []);
          setCountries(arr.map((c) => ({
            id: Number(c.id) || 0,
            name: c.name || '',
            continent: c.continent || 'Caribbean',
            info: c.info || '',
            image: toAbsUrl(c.image || ''),
            active: parseInt(c.active ?? 1)
          })));
        } catch (e) {
          setError('Failed to parse countries response');
        } finally {
          setLoading(false);
        }
      },
      controller
    );
    return () => controller.abort();
  }, []);

  if (loading) return <div className="destinations-loading">Loading destinations…</div>;
  if (error) return <div className="destinations-error">{error}</div>;

  const filtered = countries
    .filter((c) => {
      const q = query.trim().toLowerCase();
      if (!q) return true;
      return c.name.toLowerCase().includes(q) || (c.info || '').toLowerCase().includes(q);
    })
    .sort((a, b) => a.name.localeCompare(b.name));

  return (
    <div className="destinations-page">
      {/* Hero Section */}
      <div className="destinations-hero" style={{ backgroundImage: `url('https://images.unsplash.com/photo-1559827260-dc66d52bef19?q=80&w=2070&auto=format&fit=crop')` }}>
        <div className="destinations-hero-overlay"></div>
        <div className="destinations-hero-content">
          <h1 className="destinations-hero-title">Explore Caribbean Destinations</h1>
          <p className="destinations-hero-subtitle">Discover vibrant cultures, stunning beaches, and unforgettable experiences across the Caribbean islands</p>
        </div>
      </div>

      {/* Main Content */}
      <div className="destinations-content">
        <div className="destinations-header">
          <h2 className="destinations-section-title">Featured Destinations</h2>
          <div className="destinations-toolbar">
            <input
              className="destinations-search"
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search destinations..."
              aria-label="Search destinations"
            />
          </div>
        </div>

        {filtered.length === 0 ? (
          <div className="destinations-no-results">No destinations match your search.</div>
        ) : (
          <div className="destinations-grid">
            {filtered.map((c) => {
              const isActive = c.active === 1;
              const cardContent = (
                <div className="destination-card" aria-label={`View ${c.name}`} style={{ cursor: isActive ? 'pointer' : 'default', opacity: isActive ? 1 : 0.85 }}>
                  <div className="destination-card-image" style={{ backgroundImage: `url(${c.image})` }}>
                    <div className="destination-card-overlay">
                      <div className="destination-card-region">{(c.continent || 'CARIBBEAN').toUpperCase()}</div>
                      <h3 className="destination-card-name">{c.name}</h3>
                      {!isActive && (
                        <div style={{
                          marginTop: '8px',
                          background: 'rgba(0,0,0,0.7)',
                          color: '#fff',
                          padding: '6px 12px',
                          borderRadius: 4,
                          fontSize: 12,
                          fontWeight: 600,
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px'
                        }}>Coming Soon</div>
                      )}
                    </div>
                  </div>
                </div>
              );

              return isActive ? (
                <Link key={c.id} to={`/country/${slugify(c.name)}`} style={{ textDecoration: 'none' }}>
                  {cardContent}
                </Link>
              ) : (
                <div key={c.id} style={{ textDecoration: 'none' }}>
                  {cardContent}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
